import React from 'react';
import { buttonClass, theme } from '../ui/theme';

/**
 * 音乐使用授权说明弹窗组件
 * @param {boolean} open - 是否显示弹窗
 * @param {Function} onClose - 关闭回调
 */
const MusicLicenseModal = ({ open, onClose }) => {
  if (!open) return null;

  const licenseContent = [
    {
      title: '授权对象',
      content: '本授权适用于通过以下方式合法下载音乐作品的用户：月度会员、年度会员、终身会员'
    },
    {
      title: '授权内容（核心）',
      content: '对于合法下载的音乐作品，用户可在全球范围内、永久地、自由地使用，包括但不限于：',
      items: [
        '任意拷贝、保存、备份',
        '在线或线下播放、公开播出',
        '剪辑、改编、再创作',
        '用于商业项目（如视频、广告、游戏、产品、门店等）',
        '出售、再分发音乐作品或其改编版本'
      ],
      note: '上述使用行为不受版权限制，用户无需另行取得授权或支付版权费用。'
    },
    {
      title: '授权有效期',
      content: '授权自用户成功下载音乐作品之时起生效。即使会员到期或终止，已下载作品的使用权仍长期有效。'
    },
    {
      title: '署名说明',
      content: '使用音乐作品不强制要求署名或标注来源。我们欢迎但不强制用户在合适场景中标注"天流音乐网"。'
    },
    {
      title: '使用边界说明',
      content: '尽管授权范围广泛，用户仍需遵守以下基本原则：',
      items: [
        '不得将音乐作品用于任何违法用途',
        '不得虚假声明自己为音乐作品的原始作者或权利人'
      ]
    },
    {
      title: '责任隔离说明',
      content: '用户在使用、改编、传播或商业化音乐作品过程中产生的任何第三方纠纷或法律责任，均由用户自行承担，本公司不就用户的具体使用行为承担任何连带责任。'
    },
    {
      title: '补充说明',
      content: '如本授权说明与《服务条款》存在不一致之处，以《服务条款》为准。'
    }
  ];

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .music-license-modal-overlay {
          animation: fadeIn 0.2s ease-out;
        }
        .music-license-modal-content {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 music-license-modal-overlay"
        onClick={onClose}
      >
        <div 
          className={[theme.surfaces.modalPanel, 'shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden music-license-modal-content'].join(' ')}
          onClick={(e) => e.stopPropagation()}
        >
        {/* 头部 */}
        <div className="border-b border-white/10 bg-white/[0.03] px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-white">
              【音乐使用授权说明】
            </h2>
            <button
              onClick={onClose}
              className={buttonClass({ variant: 'ghost', size: 'sm', className: 'p-2 rounded-lg hover:bg-white/10' })}
              aria-label="关闭"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-white/70 mt-2">
            天流音乐网所提供的全部可下载音乐作品，均为本公司原创作品。
          </p>
        </div>

        {/* 内容区域 */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] px-6 py-5">
          <p className="text-white/70 mb-6 leading-relaxed">
            为方便用户使用，我们特此说明本网站音乐作品的使用授权范围如下：
          </p>

          <div className="space-y-6">
            {licenseContent.map((section, index) => (
              <div key={index} className="border-l-2 border-blue-500/30 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {index + 1}、{section.title}
                </h3>
                <p className="text-white/70 leading-relaxed mb-2">
                  {section.content}
                </p>
                {section.items && (
                  <ul className="list-none space-y-1.5 mt-3">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-white/70">
                        <span className="text-white/70 mr-2 mt-1">•</span>
                        <span className="flex-1 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.note && (
                  <p className="text-white/55 text-sm mt-3 italic bg-white/[0.03] rounded-lg p-3 border border-white/10">
                    {section.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="bg-white/[0.03] border-t border-white/10 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className={buttonClass({ variant: 'outline', size: 'md', className: 'px-6 py-2' })}
          >
            我已了解
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default MusicLicenseModal;
